import React from 'react';
import type {
  KeyPath,
  RecordValue,
} from '../collaboration/DataStoreRecordBehaviors';
import { isEqual } from 'lodash';
import type { Constructor, RecordId } from '../types';
import {
  EMOJI_FONT_FACES,
  getShortCodeForSkinTone,
  getShortCodeWithCurrentSkinTone,
  lookupNativeEmoji,
  parseShortCode,
} from '../utils/emoji';
import type CanvaSXBehaviors from './CanvaSXBehaviors';
import { type CanvaSXObject } from '@lib/canvasx/canvasx';
import { isNonEmptyString } from './guards';

interface IToggleReactionOptions {
  shortCode: string;
  userId: RecordId | null;
}

export type emojiSkinToneValue = 1 | 2 | 3 | 4 | 5 | 6 | null;
export interface IReactionsCollection {
  [shortCode: string]: { [userId: number]: emojiSkinToneValue };
}

export interface IReactionVotes {
  [shortCode: string]: number;
}

interface ReactionsBehaviors {
  toggleReaction: (
    options: IToggleReactionOptions,
    action?: 'add' | 'remove'
  ) => void;
  hasReaction: (options: IToggleReactionOptions) => boolean;
  reactions?: IReactionsCollection;
  emojiVotes: (shortCode: string) => number;
  userVoted: (shortCode: string) => boolean;
  reactionVotes: IReactionVotes;
  updateTooltipAfterRender: (shortCode: string) => void;
  reactionsTooltipContent: (key: string) => JSX.Element | undefined;
}

function tooltipUsers(userNames: string[], totalVotes: number): string {
  const displayUserNames = userNames.slice(0, 30);
  const extraVotes = totalVotes - displayUserNames.length;

  if (extraVotes > 0) {
    displayUserNames.push(`${extraVotes} more`);
  }

  switch (displayUserNames.length) {
    case 1:
      return displayUserNames[0];

    case 2:
      return displayUserNames.join(' and ');

    default:
      return [
        ...displayUserNames.slice(0, -1),
        `and ${displayUserNames.pop()}`,
      ].join(', ');
  }
}

export function reactionKey(shortCode: string) {
  return `reaction_${shortCode}`;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function ReactionsBehaviors(
  Base: Constructor<fabric.Object & CanvaSXObject & CanvaSXBehaviors>
): any {
  return class WithReactionBehaviors extends Base {
    __hasReactionBehaviors = true;
    serializedKeys: Array<string | RegExp>;
    valueAtPath: (keyOrPath: KeyPath) => RecordValue;
    reactions: IReactionsCollection;

    _rerenderShortCodeTooltip?: string;

    constructor(options) {
      super(options);
      this.serializedKeys?.push('reactions');

      this.on('added', () => {
        this.normalizeCurrentReactions();
      });
      this.on('duplicated', () => this.set('reactions', {}));
    }

    reactionsTooltipContent = (key: string): JSX.Element | undefined => {
      const matches = key.match('^reaction_(?<reaction>:[^:]+:)');
      if (!matches) return;

      const { reaction } = matches.groups!;
      const votes = this.valueAtPath(['reactions', reaction]);
      if (!votes) return;

      const currentVotes = Object.entries(votes).filter(([, value]) => value);

      let currentUserVoted = false;
      const userNames = currentVotes
        .map(([userId]) => {
          if (window.currentUser?.id === userId) {
            currentUserVoted = true;
            return '';
          } else {
            return this.controller.getResolvedUser(userId as RecordId)
              ?.displayName;
          }
        })
        .filter(isNonEmptyString);

      if (currentUserVoted) {
        userNames.unshift('You');
      }

      const users = tooltipUsers(userNames, currentVotes.length);
      const emoji = lookupNativeEmoji(reaction);
      return (
        <div style={{ maxWidth: '25vw' }}>
          <div
            style={{
              fontFamily: EMOJI_FONT_FACES.join(', '),
              fontSize: '4em',
              textAlign: 'center',
            }}
          >
            {emoji}
          </div>
          <div>
            {users} reacted with {reaction}
          </div>
        </div>
      );
    };

    updateTooltipAfterRender(shortCode: string) {
      this._rerenderShortCodeTooltip = shortCode;
    }

    render(ctx: CanvasRenderingContext2D): void {
      super.render(ctx);

      // If a user toggled an emoji on the shape, it will re-render the entire shape
      // and the tooltip will have closed. Re-render it immediately after the shape
      // has re-rendered.
      const shortCode = this._rerenderShortCodeTooltip;
      if (shortCode) {
        this._rerenderShortCodeTooltip = undefined;

        const key = reactionKey(shortCode);
        const mountedObject = this.mountedObjects[key];

        if (mountedObject) {
          this.showTooltip(key, mountedObject);
        }
      }
    }

    normalizeCurrentReactions(): void {
      if (!this.reactions) return;

      const isNormalized = Object.values(this.reactions).some(votes =>
        Object.values(votes).some(vote => typeof vote === 'number')
      );
      if (isNormalized) return;

      const normalizedReactions = {};
      for (const [shortCode, votes] of Object.entries(this.reactions)) {
        const shortCodeData = parseShortCode(shortCode);
        if (!shortCodeData) continue;

        const { shortCode: baseShortCode, skin } = shortCodeData;
        if (!normalizedReactions[baseShortCode]) {
          normalizedReactions[baseShortCode] = {};
        }

        const normalizedVotes = Object.entries(votes)
          .filter(([, vote]) => vote)
          .reduce(
            (votes, [userId]) => ({
              ...votes,
              [userId]: skin ? parseInt(skin, 10) : 1,
            }),
            {}
          );

        normalizedReactions[baseShortCode] = {
          ...normalizedReactions[baseShortCode],
          ...normalizedVotes,
        };
      }

      if (!isEqual(this.reactions, normalizedReactions)) {
        // Set after collaboration has been initialized.
        setTimeout(() => this.set('reactions', normalizedReactions));
      }
    }

    toggleReaction(
      { shortCode, userId }: IToggleReactionOptions,
      action?: 'add' | 'remove'
    ): void {
      const emojiData = getShortCodeWithCurrentSkinTone(shortCode);
      if (!emojiData || !userId) return;

      const { shortCode: baseShortCode, skin } = emojiData;

      const path: string[] = ['reactions', baseShortCode, userId];

      // if explicit action provided, use that to determine whether adding or removing, otherwise toggle
      const currentValue = action
        ? action === 'remove'
        : this.valueAtPath(path);

      this.set(
        path,
        // We want the value to be null for it to be deleted collaboratively
        currentValue ? null : skin
      );

      if (currentValue) {
        window.AhaAnalytics?.track(
          'Feature-7109872315285288858',
          'User deletes reaction',
          {}
        );
      } else {
        window.AhaAnalytics?.track(
          'Feature-7109872315285288858',
          'User adds reaction',
          {
            'Name of emoji': shortCode,
          }
        );
      }

      if (!currentValue) {
        this.controller.actionResolveUserInfo(userId).then(() => {
          // Nothing, just resolve
        });
      }

      this.canvas?.requestRenderAll();
    }

    hasReaction({ shortCode, userId }: IToggleReactionOptions): boolean {
      const emojiData = getShortCodeWithCurrentSkinTone(shortCode);
      if (!emojiData || !userId) return false;

      const { shortCode: baseShortCode } = emojiData;

      const path: string[] = ['reactions', baseShortCode, userId];

      return !!this.valueAtPath(path);
    }

    emojiVotes(shortCode): number {
      if (!this.reactions?.[shortCode]) return 0;

      return Object.values(this.reactions[shortCode]).filter(Boolean).length;
    }

    userVoted(shortCode): boolean {
      const emojiData = parseShortCode(shortCode);
      if (!emojiData) {
        return false;
      }

      const { shortCode: baseShortCode } = emojiData;
      if (!this.reactions?.[baseShortCode]) return false;

      const userId = this.controller?.state?.userId;
      if (!userId) return false;

      const currentVoteValue = this.valueAtPath([
        'reactions',
        baseShortCode,
        userId,
      ]);

      return !!currentVoteValue;
    }

    get reactionVotes(): IReactionVotes {
      if (!this.reactions) return {};

      const emojisWithReactions = Object.entries(this.reactions)
        .filter(([, votes]) => Object.values(votes).filter(Boolean).length > 0)
        .reduce((reactions, [shortCode, votes]) => {
          const nonNullVotes = Object.values(votes).filter(Boolean);
          const uniqueValues = new Set(nonNullVotes);
          let displayShortCode = shortCode;
          if (uniqueValues.size === 1) {
            displayShortCode =
              getShortCodeForSkinTone(shortCode, nonNullVotes[0] as number) ??
              shortCode;
          }

          reactions[displayShortCode] = nonNullVotes.length;
          return reactions;
        }, {});

      return emojisWithReactions;
    }
  };
}

export default ReactionsBehaviors;
